import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, Palette, Stack, SquaresFour, Rows } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { API } from '../config/api';
import { profileTypes, defaultColors, glassTypes, glazingBeadTypes } from '../config/productConfig';

/**
 * Impostazioni → Opzioni prodotto: the company's own profiles, colours, glass types and glazing beads.
 * They appear in the quote next to the standard ones; profiles also in the price list.
 */

const euro = (v) => (Number(v) ? `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(2)} €` : 'nessun supplemento');

const Chip = ({ children, muted }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs ${muted ? 'bg-[#F4F6F8] border-[#E2E8F0] text-[#4A5568]' : 'bg-white border-dashed border-[#0033A0] text-[#0A0F1A]'}`}>
    {children}
  </span>
);

function OptionSection({ kind, title, icon: Icon, hint, standard, options, onAdd, onDelete, withColor, placeholder }) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [hex, setHex] = useState('#9caf88');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!name.trim()) { toast.error('Scrivi il nome'); return; }
    setSaving(true);
    const ok = await onAdd(kind, { name: name.trim(), price_add: parseFloat(String(price).replace(',', '.')) || 0, ...(withColor ? { hex } : {}) });
    setSaving(false);
    if (ok) { setName(''); setPrice(''); }
  };

  return (
    <Card className="border border-[#E2E8F0] rounded-sm" data-testid={`options-${kind}`}>
      <CardHeader className="border-b border-[#E2E8F0] pb-3">
        <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
          <Icon size={18} className="text-[#0033A0]" /> {title}
        </CardTitle>
        <p className="text-xs text-[#4A5568]">{hint}</p>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {standard.map(o => (
            <Chip key={o.id} muted>
              {withColor && <span className="w-3 h-3 rounded-sm border" style={{ backgroundColor: o.hex }} />}
              {o.name}
            </Chip>
          ))}
        </div>
        {options.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {options.map(o => (
              <Chip key={o.id}>
                {withColor && <span className="w-3 h-3 rounded-sm border" style={{ backgroundColor: o.hex }} />}
                <span className="font-medium">{o.name}</span>
                <span className="text-[10px] text-[#4A5568]">{euro(o.price_add)}</span>
                <button type="button" onClick={() => onDelete(kind, o)} className="text-red-400 hover:text-red-600" title="Elimina" data-testid={`delete-${kind}-${o.id}`}>
                  <Trash size={12} />
                </button>
              </Chip>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {withColor && (
            <input type="color" value={hex} onChange={e => setHex(e.target.value)} className="h-9 w-10 rounded-sm border border-[#E2E8F0] cursor-pointer" title="Scegli il colore" data-testid={`new-${kind}-hex`} />
          )}
          <Input value={name} onChange={e => setName(e.target.value)} placeholder={placeholder} className="h-9 flex-1 min-w-[160px]"
            onKeyDown={e => { if (e.key === 'Enter') add(); }} data-testid={`new-${kind}-name`} />
          <div className="relative">
            <Input value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" placeholder="Supplemento" className="h-9 w-32 pr-6" data-testid={`new-${kind}-price`} />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[#4A5568]">€</span>
          </div>
          <Button onClick={add} disabled={saving} className="h-9 bg-[#0033A0] hover:bg-[#0047AB] text-white" data-testid={`add-${kind}`}>
            <Plus size={16} className="mr-1" /> Aggiungi
          </Button>
        </div>
        <p className="text-[11px] text-[#4A5568]">Il supplemento (anche 0) si aggiunge al prezzo di ogni pezzo, come per le voci standard.</p>
      </CardContent>
    </Card>
  );
}

export default function ProductOptionsSettings({ customProfiles, onNewProfile, onDeleteProfile }) {
  const [options, setOptions] = useState({ colors: [], glass_types: [], glazing_beads: [] });

  useEffect(() => {
    axios.get(`${API}/settings/product-options`).then(r => setOptions({ colors: [], glass_types: [], glazing_beads: [], ...r.data })).catch(() => {});
  }, []);

  const onAdd = async (kind, body) => {
    try {
      const { data } = await axios.post(`${API}/settings/product-options/${kind}`, body);
      setOptions(o => ({ ...o, [kind]: [...o[kind], data] }));
      toast.success(`"${data.name}" aggiunto: lo trovi nel preventivo`);
      return true;
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Errore nel salvataggio');
      return false;
    }
  };

  const onDelete = async (kind, o) => {
    if (!window.confirm(`Eliminare "${o.name}"? I preventivi già fatti non cambiano.`)) return;
    try {
      await axios.delete(`${API}/settings/product-options/${kind}/${o.id}`);
      setOptions(x => ({ ...x, [kind]: x[kind].filter(y => y.id !== o.id) }));
    } catch {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="space-y-4" data-testid="product-options">
      <p className="text-sm text-[#4A5568]">
        Aggiungi i profili, i colori, i vetri e i fermavetri che usi nella tua azienda: li trovi nella scheda di ogni prodotto del preventivo, insieme a quelli standard.
      </p>

      <Card className="border border-[#E2E8F0] rounded-sm" data-testid="options-profiles">
        <CardHeader className="border-b border-[#E2E8F0] pb-3 flex flex-row items-start justify-between gap-2">
          <div>
            <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
              <Stack size={18} className="text-[#0033A0]" /> Profili
            </CardTitle>
            <p className="text-xs text-[#4A5568]">Ogni profilo nuovo compare anche nel Listino prezzi, dove imposti i prezzi al m².</p>
          </div>
          <Button onClick={onNewProfile} className="h-9 bg-[#0033A0] hover:bg-[#0047AB] text-white shrink-0" data-testid="options-new-profile">
            <Plus size={16} className="mr-1" /> Nuovo profilo
          </Button>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {profileTypes.map(p => <Chip key={p.id} muted>{p.name}</Chip>)}
          </div>
          {(customProfiles || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {customProfiles.map(cp => {
                const parent = profileTypes.find(p => p.id === cp.parent_profile_id);
                return (
                  <Chip key={cp.id}>
                    <span className="font-medium">{cp.name}</span>
                    <span className="text-[10px] text-[#4A5568]">
                      {cp.pricing === 'own' ? 'prezzi propri' : `${parent?.shortName || ''} +${cp.percentage_markup}%`}
                    </span>
                    <button type="button" onClick={() => onDeleteProfile(cp.id, cp.name)} className="text-red-400 hover:text-red-600" title="Elimina" data-testid={`options-delete-profile-${cp.id}`}>
                      <Trash size={12} />
                    </button>
                  </Chip>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <OptionSection kind="colors" title="Colori" icon={Palette} withColor
        hint="Il colore scelto colora anche il telaio nei disegni del preventivo e del PDF."
        standard={defaultColors.filter(c => c.id !== 'none')} options={options.colors}
        onAdd={onAdd} onDelete={onDelete} placeholder="es. Verde Salvia RAL 6021" />
      <OptionSection kind="glass_types" title="Vetri" icon={SquaresFour}
        hint="Tipologie e categorie di vetro."
        standard={glassTypes.filter(g => g.id !== 'none')} options={options.glass_types}
        onAdd={onAdd} onDelete={onDelete} placeholder="es. Triplo vetro 4/12/4/12/4 Argon" />
      <OptionSection kind="glazing_beads" title="Fermavetri" icon={Rows}
        hint="Tipologie di fermavetro."
        standard={glazingBeadTypes} options={options.glazing_beads}
        onAdd={onAdd} onDelete={onDelete} placeholder="es. Fermavetro Squadrato" />
    </div>
  );
}
